import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Link } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { Logo } from '@/components/logo';
import { ReportProfileSheet } from '@/components/public/report-profile-sheet';
import { COMPANY } from '@/content/facts';

// `Link` com `href`: vira `<a>` na web, que o leitor da página segue.
const SOCIAL = [
  { icon: 'logo-instagram' as const, label: 'Instagram da Controlapag', url: COMPANY.instagram },
  { icon: 'logo-facebook' as const, label: 'Facebook da Controlapag', url: COMPANY.facebook },
  { icon: 'logo-linkedin' as const, label: 'LinkedIn da Controlapag', url: COMPANY.linkedin },
];

function ColumnTitle({ children }: { children: string }) {
  return <ThemedText className="mb-3 font-bold text-sm text-neutral-800">{children}</ThemedText>;
}

function FooterLink({ href, label }: { href: '/terms' | '/privacidade' | '/suporte'; label: string }) {
  return (
    <Link href={href} asChild>
      <Pressable className="py-1">
        <ThemedText className="text-sm text-neutral-500">{label}</ThemedText>
      </Pressable>
    </Link>
  );
}

/**
 * Rodapé da marca Controlapag para as páginas públicas do prestador. Layout em
 * colunas alinhado à esquerda (institucional + redes sociais), sem CTA de venda.
 */
export function PublicBrandFooter({ reportSlug }: { reportSlug?: string } = {}) {
  const year = new Date().getFullYear();
  const [reportOpen, setReportOpen] = useState(false);

  return (
    <View className="border-t border-neutral-100 bg-neutral-50 px-6 py-10">
      <View className="flex-row flex-wrap" style={{ columnGap: 40, rowGap: 28 }}>
        {/* Marca */}
        <View style={{ minWidth: 200, flexGrow: 1, flexBasis: 220 }}>
          <Link href="/" asChild>
            <Pressable accessibilityRole="link" accessibilityLabel="Controlapag, página inicial">
              <Logo variant="full" width={150} height={24} color="tint" />
            </Pressable>
          </Link>
          <Link href="/" asChild>
            <Pressable className="mt-3 max-w-xs">
              <ThemedText className="text-sm leading-5 text-neutral-500">
                Feito com Controlapag: agenda e cobrança para prestador de serviço
              </ThemedText>
            </Pressable>
          </Link>
        </View>

        {/* Institucional */}
        <View style={{ minWidth: 150 }}>
          <ColumnTitle>Institucional</ColumnTitle>
          <FooterLink href="/terms" label="Termos de uso" />
          <FooterLink href="/suporte" label="Dúvidas frequentes" />
          <FooterLink href="/privacidade" label="Política de privacidade" />
          {reportSlug ? (
            <Pressable onPress={() => setReportOpen(true)} className="py-1">
              <ThemedText className="text-sm text-neutral-500">Denunciar este perfil</ThemedText>
            </Pressable>
          ) : null}
        </View>

        {/* Redes sociais */}
        <View style={{ minWidth: 150 }}>
          <ColumnTitle>Nossas redes sociais</ColumnTitle>
          <View className="flex-row gap-3">
            {SOCIAL.map((s) => (
              <Link key={s.icon} href={s.url as never} target="_blank" rel="noopener" asChild>
                <Pressable
                  className="h-10 w-10 items-center justify-center rounded-full bg-primary/10 dark:bg-primary"
                  accessibilityRole="link"
                  accessibilityLabel={s.label}
                >
                  <Ionicons name={s.icon} size={20} className="text-primary dark:text-white" />
                </Pressable>
              </Link>
            ))}
          </View>
        </View>
      </View>

      <View className="mt-8 border-t border-neutral-200 pt-5">
        <ThemedText className="text-xs text-neutral-400">
          © {year} Controlapag. Todos os direitos reservados.
        </ThemedText>
      </View>

      {reportSlug ? (
        <ReportProfileSheet
          slug={reportSlug}
          open={reportOpen}
          onClose={() => setReportOpen(false)}
        />
      ) : null}
    </View>
  );
}
